/**
 * cancelPaymentViewModel — `(reduxState, params, refreshedPm) → props de <CancelPaymentPage>`.
 *
 * Patron B : l'étape 4 ne relit rien du store côté données. Tout ce qu'elle affiche lui vient des
 * paramètres de route posés par l'étape 3 (`CancelFeesScreen.js:148-158`), et le moyen de paiement
 * peut être remplacé par celui qu'on vient de relire (`refreshedPm`, legacy
 * `CancelPaymentScreen.js:84-86` : `refreshedPm ?? initialPm`).
 *
 * Du store il ne lit que `billingPortalUrl` (legacy `CancelPaymentScreen.js:538-540`), rempli par
 * le succès de `getSubscribePricing` — donc par le portail d'où l'on vient.
 */
import { createSelector } from "reselect"

import {
    cancelPaymentDueCents,
    cancelPaymentConfirmMessage,
    toUICancelAccessText,
    toUICancelPaymentMethod,
    toUICancelPaymentSummary,
    type ApiDefaultPaymentMethod,
    type CancelPaymentContext,
} from "../../../adapters/parent/SubscriptionCancel/subscriptionCancelAdapter"
import { eurosCents } from "../../../ui/formatters/moneyFormatter"
import { type UICancelPaymentMethod, type UICancelSummaryRow } from "../../../ui/types"

export type CancelPaymentViewModel = {
    amountLabel: string
    /** Ce que la carte paie, crédit déduit — le montant envoyé et accusé. */
    dueCents: number
    accessText: string
    summaryRows: UICancelSummaryRow[]
    paymentMethod: UICancelPaymentMethod | null
    confirmMessage: string
    /**
     * Absent → aucun portail Stripe atteignable. Les deux gestes de carte disparaissent, comme le
     * legacy remplaçait les siens par une boîte « Portail indisponible ».
     */
    billingPortalUrl: string | null
}

/** Redux root is still untyped in this app — keep the VM boundary explicit. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

type CancelPaymentContextWithPm = CancelPaymentContext & { defaultPm?: ApiDefaultPaymentMethod | null }

export const cancelPaymentViewModel = createSelector(
    [
        (state: ReduxState) => state?.data?.formData?.billingPortalUrl ?? null,
        (_state: ReduxState, context: CancelPaymentContextWithPm) => context,
        (_state: ReduxState, _context: CancelPaymentContextWithPm, refreshedPm: ApiDefaultPaymentMethod | null) =>
            refreshedPm,
    ],
    (billingPortalUrl, context, refreshedPm): CancelPaymentViewModel => {
        const dueCents = cancelPaymentDueCents(context)
        return {
            amountLabel: eurosCents(dueCents),
            dueCents,
            accessText: toUICancelAccessText(context),
            summaryRows: toUICancelPaymentSummary(context),
            paymentMethod: toUICancelPaymentMethod(refreshedPm ?? context.defaultPm),
            confirmMessage: cancelPaymentConfirmMessage(context),
            billingPortalUrl,
        }
    }
)
